/**
 * Download/push format catalog shared by the single Download Model modal.
 *
 * Local download formats are always available; registry push targets are
 * only offered when the organization has credentials/targets configured
 * (see `missingConfigMessage`).
 */

export const DOWNLOAD_ARTIFACT_TYPES = ['zip', 'docker'];
export const PUSH_ARTIFACT_TYPES = ['ecr', 'gcr', 'dockerhub', 'acr'];

/** Format cards for the two local download formats (Figma 9952:24591). */
export const DOWNLOAD_FORMAT_CARDS = [
  { value: 'zip', label: 'Model Raw Files', iconSlug: 'folder-code' },
  { value: 'docker', label: 'Docker Image', iconSlug: 'docker' },
];

export const PUSH_ARTIFACT_LABELS = {
  ecr: 'Push to ECR',
  gcr: 'Push to GCR',
  dockerhub: 'Push to Docker Hub',
  acr: 'Push to Azure ACR',
};

const PUSH_ARTIFACT_ICONS = {
  ecr: 'cloud-upload',
  gcr: 'cloud-upload',
  dockerhub: 'docker',
  acr: 'cloud-upload',
};

export const NO_PUSH_TARGETS_MESSAGE =
  'No registry push targets are configured. Add push targets in Account Settings first.';

export function isPushArtifactType(artifactType) {
  return PUSH_ARTIFACT_TYPES.includes(artifactType);
}

/**
 * Returns null if the provider is configured, or a short error string if not.
 * `settings` is the raw get_organization_settings response (may be null while loading).
 */
export function missingConfigMessage(artifactType, settings) {
  if (!settings) return null; // still loading — don't block
  switch (artifactType) {
    case 'ecr':
      if (!(settings.aws_ecr_targets || []).some((t) => t?.uri))
        return 'Configure AWS - ECR targets in Account Settings first.';
      return null;
    case 'gcr':
      if (!(settings.gcp_gcr_targets || []).some((t) => t?.uri))
        return 'Configure GCP - GCR targets in Account Settings first.';
      return null;
    case 'dockerhub':
      if (
        !(settings.dockerhub_username || '').trim() ||
        !(settings.dockerhub_targets || []).some((t) => t?.repository)
      )
        return 'Configure Docker Hub credentials and targets in Account Settings first.';
      return null;
    case 'acr':
      if (!(settings.azure_acr_targets || []).some((t) => t?.uri))
        return 'Configure Azure - ACR targets in Account Settings first.';
      return null;
    default:
      return null;
  }
}

/** Push targets the organization has configured, in catalog order. */
export function configuredPushTypes(settings) {
  if (!settings) return [];
  return PUSH_ARTIFACT_TYPES.filter((type) => !missingConfigMessage(type, settings));
}

/**
 * Format cards to show for a given mode:
 *  - `'all'`: both download formats plus every configured push target.
 *  - `'push'`: configured push targets only.
 * While `settings` is still loading (`null`) push cards are not shown yet.
 */
export function formatCardsForMode(formatMode, settings) {
  const pushCards = configuredPushTypes(settings).map((type) => ({
    value: type,
    label: PUSH_ARTIFACT_LABELS[type],
    iconSlug: PUSH_ARTIFACT_ICONS[type],
  }));
  if (formatMode === 'push') return pushCards;
  return [...DOWNLOAD_FORMAT_CARDS, ...pushCards];
}
